import {Inject, Injectable} from '@angular/core';
import {Observable, of} from 'rxjs';
import {catchError} from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import {AttorneyClientSettings, Organization, PermissionGroup, UserCountBreakdown} from '@ee/common/models';
import {AccountType} from '@ee/common/enums';

@Injectable({providedIn: 'root'})
export class OrganizationService {
  constructor(private http: HttpClient, @Inject('environment') private environment) {
  }

  getAllOrganizations(type: AccountType): Observable<Organization[]> {
    const params = {
      type: type
    };
    return this.http.get<Organization[]>(`${this.environment.api_prefix}api/organizations`, { params });
  }

  updateOrganization(organization: Organization): Observable<Organization> {
    return this.http.put<Organization>(`${this.environment.api_prefix}api/organizations`, organization);
  }

  generateAttorneyLogoUrl(subdomain: string): Observable<string | undefined> {
    return this.http.get(`${this.environment.api_prefix}api/organizations/attorney/${subdomain}/logo`, {responseType: 'text'}).pipe(
      catchError(() => of(undefined))
    );
  }

  uploadOrganizationLogo(formData: FormData): Observable<any> {
    return this.http.post(`${this.environment.api_prefix}api/organizations/upload-logo`, formData, {responseType: 'text'});
  }

  getAttorneyByIdentifier(attorneyIdentifier: string): Observable<Organization> {
    return this.http.get<Organization>(
      `${this.environment.api_prefix}api/organizations/attorney/${attorneyIdentifier}`);
  }

  getBillingDescriptionHistory(): Observable<string[]> {
    return this.http.get<string[]>(`${this.environment.api_prefix}api/organizations/billable-description-history`);
  }

  updateBillableDescriptionHistory(descriptions: string[]): Observable<string[]> {
    return this.http.put<string[]>(`${this.environment.api_prefix}api/organizations/billable-description-history`, descriptions);
  }

  updateEvictionStatusUpdate(key: string, value: any): Observable<AttorneyClientSettings> {
    return this.http.post<AttorneyClientSettings>(`${this.environment.api_prefix}api/organizations/case-status-update`, {
      key: key,
      value: value
    });
  }

  getAttorneys(): Observable<Organization[]> {
    return this.http.get<Organization[]>(`${this.environment.api_prefix}api/organizations/attorneys`);
  }

  getDefaultPermissionGroups(type: AccountType): Observable<PermissionGroup[]> {
    return this.http.get<PermissionGroup[]>(`${this.environment.api_prefix}api/organizations/permission-groups/default/${type}`);
  }

  getPermissionGroups(): Observable<PermissionGroup[]> {
    return this.http.get<PermissionGroup[]>(`${this.environment.api_prefix}api/organizations/permission-groups`);
  }

  updateSubscriptionUserCount(organizationId: string, subscriptionUserCount: number): Observable<Organization> {
    return this.http.put<Organization>(`${this.environment.api_prefix}api/organizations/${organizationId}/subscription-user-count`, {
      subscription_user_count: subscriptionUserCount
    });
  }

  updateSubscriptionStatus(organizationId: string, subscriptionStatus: string): Observable<Organization> {
    return this.http.put<Organization>(`${this.environment.api_prefix}api/organizations/${organizationId}/subscription-status`, {
      status: subscriptionStatus
    });
  }

  updateTrialEndDate(organizationId: string, trialEndDate: string): Observable<Organization> {
    return this.http.put<Organization>(`${this.environment.api_prefix}api/organizations/${organizationId}/trial-end-date`, {
      subscription_trial_end_date: trialEndDate
    });
  }

  toggleEnforceMfa(): Observable<void> {
    return this.http.post<void>(`${this.environment.api_prefix}api/organizations/toggle-enforce-mfa`, {});
  }

  getCurrentOrganization(): Observable<Organization> {
    return this.http.get<Organization>(`${this.environment.api_prefix}api/organizations/current`);
  }  

  /**
   * Get user count breakdown for billing display
   * Returns breakdown of active users, pending users, and pending invites
   * Requirements: 3.4, 4.1
   */
  getUserCountBreakdown(organizationId: string): Observable<UserCountBreakdown> {
    return this.http.get<UserCountBreakdown>(`${this.environment.api_prefix}api/organizations/${organizationId}/user-count-breakdown`);
  }

}
